import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimWorkOrderObjectSelectionData, BimWorkOrderObjectSelectionDataFilterInput } from 'gql/graphql';
import type { TConstructionTypesIndexes } from 'pages/Project/Common/types';
import { defaultCounts } from 'pages/Project/Common/WorkOrderStructure/consts';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderObjectSelectionListIsIncludedGql } from '../../bimWorkOrderQueries';

interface IUseIncludedCountByConstructionType {
    workOrderId?: number;
}

const sumReducer = (a: number, i: any) => a + (i.isIncluded ? 1 : 0);

const mapInitialData = (initialData: BimWorkOrderObjectSelectionData[]) =>
    Object.keys(defaultCounts).reduce((acc, key) => {
        acc[key as TConstructionTypesIndexes] = initialData
            .filter((i) => String(i.constructionType.id) === key)
            .reduce(sumReducer, 0);

        return acc;
    }, defaultCounts);

const requestId = 'bimWorkOrderObjectSelectionListIsIncluded';

export const useIncludedCountByConstructionType = (props: IUseIncludedCountByConstructionType) => {
    const dispatch = useDispatch();
    const { workOrderId } = props;

    const [includedCount, setIncludedCount] = useState<Record<TConstructionTypesIndexes, number>>();

    const [
        bimWorkOrderObjectSelectionListIsIncludedData,
        bimWorkOrderObjectSelectionListIsIncludedStatus,
        ,
        bimWorkOrderObjectSelectionListIsIncludedRequest,
    ] = useRequest({
        requestId,
        query: bimWorkOrderObjectSelectionListIsIncludedGql,
        withEffect: false,
    });

    const [bimWorkOrderObjectSelectionListIsIncludedReset] = useResetStoreData({ requestId });

    const refreshIncludedCount = useCallback(
        (constructionTypeId: number) => {
            const where: BimWorkOrderObjectSelectionDataFilterInput = {
                constructionType: { id: { eq: constructionTypeId } },
            };

            dispatch(bimWorkOrderObjectSelectionListIsIncludedRequest({ workOrderId, where }));
        },
        [bimWorkOrderObjectSelectionListIsIncludedRequest, dispatch, workOrderId],
    );

    useEffect(() => {
        if (
            bimWorkOrderObjectSelectionListIsIncludedStatus === RequestStatus.SUCCESS &&
            bimWorkOrderObjectSelectionListIsIncludedData?.bimWorkOrderObjectSelectionList?.items?.length
        ) {
            const items = bimWorkOrderObjectSelectionListIsIncludedData?.bimWorkOrderObjectSelectionList?.items;

            if (includedCount) {
                const constructionTypeId = items[0].constructionType.id;

                setIncludedCount({
                    ...includedCount,
                    [constructionTypeId]: items.reduce(sumReducer, 0),
                });
            } else {
                setIncludedCount(mapInitialData(items as BimWorkOrderObjectSelectionData[]));
            }

            bimWorkOrderObjectSelectionListIsIncludedReset(undefined);
        }
    }, [
        bimWorkOrderObjectSelectionListIsIncludedData?.bimWorkOrderObjectSelectionList?.items,
        bimWorkOrderObjectSelectionListIsIncludedReset,
        bimWorkOrderObjectSelectionListIsIncludedStatus,
        includedCount,
    ]);

    useEffect(() => {
        dispatch(bimWorkOrderObjectSelectionListIsIncludedRequest({ workOrderId }));
    }, [
        bimWorkOrderObjectSelectionListIsIncludedRequest,
        bimWorkOrderObjectSelectionListIsIncludedReset,
        dispatch,
        workOrderId,
    ]);

    return { includedCount, refreshIncludedCount, setIncludedCount };
};
